/**
 * Si un documento admite nota de crédito.
 *
 * Venía decidiéndose con `isEmpty(Pre_Assigned_No)`, y ese atajo mezcla dos
 * cosas distintas:
 *
 * - Para una factura **creada en el terminal** sí significa «todavía no se ha
 *   recuperado el número definitivo», y lo documenta `getFinalDocumentNo`.
 * - Para una **traída del histórico** no significa nada: una factura que nunca
 *   nació como borrador del terminal no tiene número pre-asignado en Business
 *   Central, y el pull escribe `""`. Medido el 04/09/2026: **100.730 de las
 *   140.837** facturas de caja de Costa Rica y **32.576 de 100.559** de Paraguay
 *   están así. En Honduras y El Salvador el campo ni siquiera se publica en la
 *   entidad por la que se leen las registradas, así que el bloqueo es
 *   permanente. En Ecuador son 0, y por eso el defecto no se veía ahí.
 *
 * La autoridad es `posted`, que lo escribe el propio pull y no depende de que
 * Business Central publique nada.
 *
 * Pero estar contabilizado es **necesario y no suficiente**. Business Central
 * enumeró seis documentos que están en `PostedSalesInvoice` y no son ventas; el
 * que importa es el sello de «Documento borrado» del acuerdo
 * `contrato-documento-borrado-y-pagos-huerfanos`: en Costa Rica hay tres
 * --`VFR2-002612`, `VFR4-004071`, `VFR6-007173`-- que son `Venta_TPV`, están en
 * una tienda real, se llevaron un consecutivo fiscal y valen cero. Sin las
 * exclusiones, los tres admitirían una nota de crédito de cero sobre algo que
 * nunca fue una venta.
 *
 * Acordado en `contrato-nota-de-credito-sobre-historicos`, rondas 2 a 4.
 */
import db from "../db/database";
import { tieneFormaDeSello } from "./selloDeDocumentoBorrado";

/**
 * Los campos que el pull tiene que guardar para poder decidir después.
 *
 * `mapPostedInvoice` no persistía ninguno --comprobado--, así que la exclusión
 * no se podía aplicar aunque se conociera. Van con `?? undefined` y no `?? ""`
 * a propósito: donde Business Central no publica el campo, escribir un valor
 * encima sería inventarse una respuesta. Ausente significa «no se sabe», y eso
 * lo resuelve el respaldo estructural.
 */
export const camposDeExclusion = (apiObj) => ({
  DocumentoBorradoTPV: apiObj.DocumentoBorradoTPV ?? undefined,
  Cancelled: apiObj.Cancelled ?? undefined,
  Corrective: apiObj.Corrective ?? undefined,
  Anulado_TPV: apiObj.Anulado_TPV ?? undefined,
  Anulado_por_Documento: apiObj.Anulado_por_Documento ?? undefined,
  Anulados: apiObj.Anulados ?? undefined,
  Oculto: apiObj.Oculto ?? undefined,
});

/** Cada exclusión con el texto que se le enseña a quien pulsó el botón. */
const EXCLUSIONES = [
  [(d) => d.DocumentoBorradoTPV === true, "es el rastro de un documento borrado, no una venta"],
  [(d) => d.Cancelled === true, "está anulado"],
  [(d) => d.Corrective === true, "es un documento correctivo"],
  [(d) => d.Anulado_TPV === true, "está anulado desde el punto de venta"],
  [(d) => String(d.Anulado_por_Documento ?? "").trim() !== "", "ya fue anulado por otro documento"],
  [(d) => d.Anulados === true, "está marcado como anulado"],
  [(d) => d.Oculto === true, "está oculto"],
];

/**
 * Por qué este documento no admite nota de crédito, o `null` si la admite.
 *
 * @param {object} documento Factura local, tal como la dejó el pull o el alta.
 * @param {{importeDeLineas?: number, importeDePagos?: number}} [medidas]
 *   Sumas del documento, para el respaldo estructural. Sólo hacen falta donde
 *   Business Central no publica `DocumentoBorradoTPV` --Honduras, El Salvador,
 *   y cualquier ambiente anterior a la 1.0.4.96, que hoy son las cuatro
 *   producciones--.
 * @returns {string|null}
 */
export const motivoParaNoAbonar = (documento, medidas = {}) => {
  if (!documento) return "no se encontró la factura";

  const contabilizada =
    documento.posted === 1 || String(documento.Pre_Assigned_No ?? "").trim() !== "";
  if (!contabilizada) return "todavía no está contabilizada";

  for (const [cumple, motivo] of EXCLUSIONES) {
    if (cumple(documento)) return motivo;
  }

  // Respaldo estructural, para donde el campo no viaja. Es la misma forma que
  // reconoce un sello sin preguntarle a Business Central: registrado, líneas en
  // cero y pagos que no lo están.
  if (
    documento.DocumentoBorradoTPV === undefined &&
    tieneFormaDeSello(true, medidas.importeDeLineas ?? 0, medidas.importeDePagos ?? 0)
  ) {
    return "no tiene importe y sí cobros: parece el rastro de un documento borrado";
  }

  return null;
};

/** Si admite nota de crédito. Azúcar sobre `motivoParaNoAbonar`. */
export const admiteNotaDeCredito = (documento, medidas) =>
  motivoParaNoAbonar(documento, medidas) === null;

/**
 * Las dos sumas que necesita el respaldo estructural.
 *
 * Sólo se calculan cuando hacen falta --cuando `DocumentoBorradoTPV` no viajó--
 * porque leer las líneas y los pagos de un documento para una comprobación que
 * casi siempre se resuelve con un booleano es trabajo que no hay que hacer.
 *
 * @param {object} documento Factura local, con o sin `lines` ya cargadas.
 */
export const medidasDelDocumento = async (documento) => {
  if (!documento || documento.DocumentoBorradoTPV !== undefined) return {};

  const suma = (filas, campo) =>
    (filas ?? []).reduce((total, fila) => total + (Number(fila?.[campo]) || 0), 0);

  const lineas =
    documento.lines ??
    (await db.invoiceLines
      .where("invoiceId")
      .equals(documento.id)
      .and((l) => !l.deleted)
      .toArray()
      .catch(() => []));

  const numero = String(documento.Pre_Assigned_No || documento.No || "").trim();
  const pagos = numero
    ? await db.invoicePaymentLines
        .where("Document_No_")
        .equals(numero)
        .and((p) => !p.deleted)
        .toArray()
        .catch(() => [])
    : [];

  return {
    importeDeLineas: suma(lineas, "Line_Amount"),
    importeDePagos: suma(pagos, "Amount") || suma(pagos, "Importe"),
  };
};
